<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="false"
                :showAddNew="false"
                title="Task Calendar"
            />
        </div>

        <div class="px-2 pb-2">
          <div class="row">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label"></label>
              <v-select
                  placeholder="Select Type"
                  :options="taskCalendarFilterList"
                  v-model="taskCalendarType"
                  label="name"
                  :reduce="name => name.value"
              />
            </div>

            <div class="col-sm-2 col-md-2 d-flex align-items-end mt-2 action-btn-setter">
              <button
                  style="min-width: 64px;"
                  @click="onEventSubmitted"
                  class="btn btn-primary btn-block waves-effect waves-float waves-light"
              >GO
              </button>
            </div>
          </div>
        </div>

        <div class="app-calendar overflow-hidden border">
            <div class="row no-gutters">
                <div class="col position-relative">
                    <div class="card shadow-none border-0 mb-0 rounded-0">
                        <div class="card-body pb-0" v-if="!loading">
                          <CalendarComponent
                              :defaultView="'timeGridWeek'"
                              :calendarOptions="calendarOptions"
                              ref="calendarRef"
                          />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Loader v-if="loading"/>
        <TaskEventScheduler
            v-if="store.state.isModalOpen"
            :startDate="startDate"
            :endDate="endDate"
            :editData="singleActivity"
            :taskStatus="taskStatus"
            :markAsDoneShow="markAsDoneShow"
            :isCompleted="isCompleted"
            @submitted="onEventSubmitted"
        />
    </div>
</template>

<script setup>
import {ref, reactive, inject, onMounted} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import CalendarComponent from '@/components/atom/calendar/CalendarComponent.vue';
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import TaskEventScheduler from '@/components/molecule/company/admin/TaskEventScheduler.vue';
import { getCurrentDateTime } from '@/services/utils/global.js';
import handleActivity from "@/services/modules/procurement/activity";
import handleChartAccount from '@/services/modules/chartAccount'

const { fetchTaskActivityLog, fetchSingleTaskActivityLog } = handleActivity()
const {fetchHome} = handleChartAccount()

const showError = inject('showError');
const showSuccess = inject('showSuccess');

const route = useRoute();
const router = useRouter();
const store = useStore();

const loading = ref(false);
const currentEvents = ref([]);
const singleActivity = ref(null);
const taskStatus = ref({});
const markAsDoneShow = ref(false);
const isCompleted = ref(false);
const calendarRef = ref(false);
const taskCalendarFilterList = ref([]);
const taskCalendarType = ref('');
const startDate = ref('');
const endDate = ref('');

const calendarOptions = reactive({
  initialEvents: [],
  editable: true,
  selectable: true,
  selectMirror: true,
  dayMaxEvents: true,
  weekends: true,
  select: handleDateSelect,
  eventClick: handleEventClick,
  eventsSet: handleEvents,
  events: async function(info, successCallback, failureCallback) {
    try {
      let selectedMonthStart = info.startStr.substring(0, 10);
      let selectedMonthEnd = info.endStr.substring(0, 10);

      let query = getQuery();
      if (selectedMonthStart) query += '&start_date=' + selectedMonthStart;
      if (selectedMonthEnd) query += '&end_date=' + selectedMonthEnd;

      const res = await fetchTaskActivityLog(query);

      if (!res.status) {
        successCallback([]);
        return;
      }

      const events = res.data.map(item => ({
        id: item.id,
        title: item.short_description,
        start: getCurrentDateTime(item.start_time),
        end: item.end_time ? getCurrentDateTime(item.end_time) : null,
      }));

      successCallback(events);
    } catch (error) {
      failureCallback(error);
    }
  }
});

const onEventSubmitted = () => {
  let calendarApi = null;
  if (
      calendarRef.value &&
      typeof calendarRef.value.getApi === 'function'
  ) {
    calendarApi = calendarRef.value.getApi();
  }

  if (calendarApi) {
    calendarApi.refetchEvents();
  }
};

const getQuery = () => {
  const companyId = route.params.companyId;
  const isMySchedule = route.query.from === 'my-visit-schedule';
  let query = `?company_id=${companyId}&route_name=task-calendar`;
  if (isMySchedule) query += `&my_plan=1`;
  if (taskCalendarType.value) query += `&task_calendar_type=${taskCalendarType.value}`;
  return query;
}

function handleDateSelect(selectInfo) {
  const selectedDate = new Date(selectInfo.start);
  const now = new Date();

  if (selectInfo.allDay){
    selectedDate.setHours(now.getHours());
    selectedDate.setMinutes(now.getMinutes());
  }

  const selectedEnd = new Date(selectedDate);
  selectedEnd.setHours(selectedDate.getHours() + 1);

  const formatDateTime = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  startDate.value = formatDateTime(selectedDate);
  endDate.value = formatDateTime(selectedEnd);
  singleActivity.value = null;
  taskStatus.value = {};
  markAsDoneShow.value = false;
  isCompleted.value = false;
  store.state.isModalOpen = true;
}

async function handleEventClick(clickInfo) {
  const activityId = clickInfo.event.id;
  if (!activityId) return;
  markAsDoneShow.value = false;
  isCompleted.value = false;
  loading.value = true;

  try {
    const res = await fetchSingleTaskActivityLog(activityId, getQuery());
    if (!res.status) return showError(res.message);
    let response = res.data;
    singleActivity.value = response;
    taskStatus.value = res.task_status;
    markAsDoneShow.value = true;
    if (['completed', 'approved'].includes(response.status)) {
      isCompleted.value = true;
    }
    store.state.isModalOpen = true;
  } finally {
    loading.value = false;
  }
}

function handleEvents(events) {
  currentEvents.value = events;
}

onMounted(() => {
  Promise.all([
    fetchHome().then(res => {
      if (!res.data) {
        return;
      }
      if (res.data && res.data.task_cander_filter){
        taskCalendarFilterList.value = res.data.task_cander_filter;
      }
    })
  ])

})

</script>
